import { GeckoEntryBase } from 'types/entries/geckoEntry'
import {
  RolodexEntryJson,
  RolodexEntryType,
} from 'types/rolodex/rolodexEntries'
import {
  parseArray,
  parseBoolean,
  parseFiniteNumber,
  parseString,
} from 'utils/parsingHelpers'

export const KestrelDetectionSegmentType = {
  Mask: 'mask',
  Bbox: 'bbox',
} as const

export type KestrelDetectionSegmentType =
  (typeof KestrelDetectionSegmentType)[keyof typeof KestrelDetectionSegmentType]

export type KestrelDetectionBbox = [number, number, number, number]

export const KestrelDetectionSegmentPolarity = {
  Positive: 'positive',
  Negative: 'negative',
} as const

export type KestrelDetectionSegmentPolarity =
  (typeof KestrelDetectionSegmentPolarity)[keyof typeof KestrelDetectionSegmentPolarity]

export interface KestrelDetectionTrainingSegment {
  id: string
  className: string
  type: KestrelDetectionSegmentType
  bbox: KestrelDetectionBbox
  mask?: string
  polarity?: KestrelDetectionSegmentPolarity
  createdAt: string
}

export interface KestrelDetectionTrainingImage {
  filename: string
  width: number
  height: number
  segments: KestrelDetectionTrainingSegment[]
  completed: boolean
}

export type KestrelDetectionTrainingImages = Record<
  string,
  KestrelDetectionTrainingImage
>

export const KestrelTrainingDatasetExportState = {
  Submitted: 'submitted',
  Copying: 'copying',
  Importing: 'importing',
  Created: 'created',
  Failed: 'failed',
} as const

export type KestrelTrainingDatasetExportState =
  (typeof KestrelTrainingDatasetExportState)[keyof typeof KestrelTrainingDatasetExportState]

export const KestrelTrainingDatasetExportObjective = {
  ObjectDetection: 'object_detection',
  Segmentation: 'segmentation',
} as const

export type KestrelTrainingDatasetExportObjective =
  (typeof KestrelTrainingDatasetExportObjective)[keyof typeof KestrelTrainingDatasetExportObjective]

export interface KestrelTrainingDatasetExport {
  id: string
  display_name?: string
  objective?: KestrelTrainingDatasetExportObjective
  state?: KestrelTrainingDatasetExportState
  created_at?: string
  error?: string
  dataset_resource_name?: string
  image_count?: number
  execution_name?: string
}

export const KestrelTrainingModelState = {
  Submitted: 'submitted',
  Restaging: 'restaging',
  Training: 'training',
  Ready: 'ready',
  Failed: 'failed',
  Cancelled: 'cancelled',
} as const

export type KestrelTrainingModelState =
  (typeof KestrelTrainingModelState)[keyof typeof KestrelTrainingModelState]

export interface KestrelTrainingModel {
  id: string
  display_name?: string
  state?: KestrelTrainingModelState
  created_at?: string
  dataset_entry_id?: string
  dataset_display_name?: string
  objective?: KestrelTrainingDatasetExportObjective
  node_hours?: number
  model_type?: string
  model_resource_name?: string
  training_pipeline_name?: string
  error?: string
}

export type KestrelDetectionTrainingData = {
  classes: string[]
  images: KestrelDetectionTrainingImages
  exports: KestrelTrainingDatasetExport[]
  models: KestrelTrainingModel[]
}

const parseRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

const parseBbox = (value: unknown): KestrelDetectionBbox | undefined => {
  const items = parseArray<unknown>(value)
  if (!items || items.length !== 4) return undefined
  const x1 = parseFiniteNumber(items[0])
  const y1 = parseFiniteNumber(items[1])
  const x2 = parseFiniteNumber(items[2])
  const y2 = parseFiniteNumber(items[3])
  if (
    x1 === undefined ||
    y1 === undefined ||
    x2 === undefined ||
    y2 === undefined
  ) {
    return undefined
  }
  return [x1, y1, x2, y2]
}

const parseSegmentType = (
  value: unknown
): KestrelDetectionSegmentType | undefined => {
  const type = parseString(value)
  return Object.values(KestrelDetectionSegmentType).includes(
    type as KestrelDetectionSegmentType
  )
    ? (type as KestrelDetectionSegmentType)
    : undefined
}

const parsePolarity = (
  value: unknown
): KestrelDetectionSegmentPolarity | undefined => {
  const polarity = parseString(value)
  return Object.values(KestrelDetectionSegmentPolarity).includes(
    polarity as KestrelDetectionSegmentPolarity
  )
    ? (polarity as KestrelDetectionSegmentPolarity)
    : undefined
}

const parseSegment = (
  value: unknown
): KestrelDetectionTrainingSegment | undefined => {
  const record = parseRecord(value)
  const id = parseString(record.id)
  const className = parseString(record.className)
  const type = parseSegmentType(record.type)
  const bbox = parseBbox(record.bbox)
  const createdAt = parseString(record.createdAt)
  if (!id || !className || !type || !bbox || !createdAt) return undefined
  const polarity = parsePolarity(record.polarity)
  return {
    id,
    className,
    type,
    bbox,
    mask: parseString(record.mask),
    createdAt,
    ...(polarity ? { polarity } : {}),
  }
}

const parseSegments = (value: unknown): KestrelDetectionTrainingSegment[] => {
  const items = parseArray<unknown>(value) ?? []
  return items
    .map(parseSegment)
    .filter((segment): segment is KestrelDetectionTrainingSegment => !!segment)
}

const parseImage = (
  filename: string,
  value: unknown
): KestrelDetectionTrainingImage | undefined => {
  const record = parseRecord(value)
  const parsedFilename = parseString(record.filename) ?? filename
  const width = parseFiniteNumber(record.width)
  const height = parseFiniteNumber(record.height)
  if (!parsedFilename || width === undefined || height === undefined) {
    return undefined
  }
  return {
    filename: parsedFilename,
    width,
    height,
    segments: parseSegments(record.segments),
    completed: parseBoolean(record.completed) ?? false,
  }
}

const parseImages = (value: unknown): KestrelDetectionTrainingImages => {
  const images: KestrelDetectionTrainingImages = {}
  for (const [filename, rawImage] of Object.entries(parseRecord(value))) {
    const image = parseImage(filename, rawImage)
    if (image) images[filename] = image
  }
  return images
}

const parseClasses = (value: unknown): string[] => {
  const items = parseArray<unknown>(value) ?? []
  const classes: string[] = []
  for (const item of items) {
    const className = parseString(item)?.trim()
    if (className && !classes.includes(className)) {
      classes.push(className)
    }
  }
  return classes
}

const parseKestrelTrainingDatasetExportState = (
  value: unknown
): KestrelTrainingDatasetExportState | undefined => {
  const state = parseString(value)
  return Object.values(KestrelTrainingDatasetExportState).includes(
    state as KestrelTrainingDatasetExportState
  )
    ? (state as KestrelTrainingDatasetExportState)
    : undefined
}

const parseKestrelTrainingDatasetExportObjective = (
  value: unknown
): KestrelTrainingDatasetExportObjective | undefined => {
  const objective = parseString(value)
  return Object.values(KestrelTrainingDatasetExportObjective).includes(
    objective as KestrelTrainingDatasetExportObjective
  )
    ? (objective as KestrelTrainingDatasetExportObjective)
    : undefined
}

export const parseKestrelTrainingDatasetExport = (
  value: unknown
): KestrelTrainingDatasetExport | undefined => {
  const record = parseRecord(value)
  const id = parseString(record.id)
  if (!id) return undefined
  return {
    id,
    display_name: parseString(record.display_name),
    objective: parseKestrelTrainingDatasetExportObjective(record.objective),
    state:
      parseKestrelTrainingDatasetExportState(record.state) ??
      parseKestrelTrainingDatasetExportState(record.step),
    created_at: parseString(record.created_at),
    error: parseString(record.error),
    dataset_resource_name: parseString(record.dataset_resource_name),
    image_count: parseFiniteNumber(record.image_count),
    execution_name: parseString(record.execution_name),
  }
}

export const parseKestrelTrainingDatasetExports = (
  value: unknown
): KestrelTrainingDatasetExport[] => {
  const items = parseArray<unknown>(value) ?? []
  return items
    .map(parseKestrelTrainingDatasetExport)
    .filter((item): item is KestrelTrainingDatasetExport => !!item)
}

const parseKestrelTrainingModelState = (
  value: unknown
): KestrelTrainingModelState | undefined => {
  const state = parseString(value)
  return Object.values(KestrelTrainingModelState).includes(
    state as KestrelTrainingModelState
  )
    ? (state as KestrelTrainingModelState)
    : undefined
}

export const parseKestrelTrainingModel = (
  value: unknown
): KestrelTrainingModel | undefined => {
  const record = parseRecord(value)
  const id = parseString(record.id)
  if (!id) return undefined
  return {
    id,
    display_name: parseString(record.display_name),
    state: parseKestrelTrainingModelState(record.state),
    created_at: parseString(record.created_at),
    dataset_entry_id: parseString(record.dataset_entry_id),
    dataset_display_name: parseString(record.dataset_display_name),
    objective: parseKestrelTrainingDatasetExportObjective(record.objective),
    node_hours: parseFiniteNumber(record.node_hours),
    model_type: parseString(record.model_type),
    model_resource_name: parseString(record.model_resource_name),
    training_pipeline_name: parseString(record.training_pipeline_name),
    error: parseString(record.error),
  }
}

export const parseKestrelTrainingModels = (
  value: unknown
): KestrelTrainingModel[] => {
  const items = parseArray<unknown>(value) ?? []
  return items
    .map(parseKestrelTrainingModel)
    .filter((item): item is KestrelTrainingModel => !!item)
}

/**
 * How a model reaches the user, which is also how it is permission checked.
 * Catalog models inherit their Kestrel model's Rolodex ACL. Global models
 * belong to no Kestrel model and are gated by role instead.
 */
export const KestrelModelSource = {
  Catalog: 'catalog',
  Global: 'global',
} as const

export type KestrelModelSource =
  (typeof KestrelModelSource)[keyof typeof KestrelModelSource]

const parseKestrelModelSource = (value: unknown): KestrelModelSource => {
  const source = parseString(value)
  return Object.values(KestrelModelSource).includes(
    source as KestrelModelSource
  )
    ? (source as KestrelModelSource)
    : KestrelModelSource.Catalog
}

/**
 * One model the user may use. Unlike KestrelTrainingModel, which is a record
 * stored on a single training entry, these rows come from the server's runnable
 * set: AutoML pipelines plus any shared models the user's roles allow.
 */
export interface KestrelAutoMLCatalogRow {
  id: string
  state?: KestrelTrainingModelState
  display_name?: string
  source: KestrelModelSource
  training_pipeline_name?: string
  model_resource_name?: string
  /** Unset for global models, which have no owning Kestrel model. */
  kestrel_model_id?: string
  kestrel_model_name?: string
  dataset_display_name?: string
  objective?: KestrelTrainingDatasetExportObjective
  node_hours?: number
  /** False when the pipeline predates Labeling Studio tracking. */
  tracked?: boolean
  created_at?: string
  updated_at?: string
  error?: string
}

export const parseKestrelAutoMLCatalogRow = (
  value: unknown
): KestrelAutoMLCatalogRow | undefined => {
  const record = parseRecord(value)
  const id = parseString(record.id)
  if (!id) return undefined
  const source = parseKestrelModelSource(record.source)
  const kestrelModelId = parseString(record.kestrel_model_id)
  // A catalog row is gated by its Kestrel model, so one without that id was
  // never permission checked and has nothing to link back to. Global rows are
  // gated by role and legitimately have no owning entry.
  if (source === KestrelModelSource.Catalog && !kestrelModelId) return undefined
  return {
    id,
    state: parseKestrelTrainingModelState(record.state),
    display_name: parseString(record.display_name),
    source,
    training_pipeline_name: parseString(record.training_pipeline_name),
    model_resource_name: parseString(record.model_resource_name),
    kestrel_model_id: kestrelModelId,
    kestrel_model_name: parseString(record.kestrel_model_name),
    dataset_display_name: parseString(record.dataset_display_name),
    objective: parseKestrelTrainingDatasetExportObjective(record.objective),
    node_hours: parseFiniteNumber(record.node_hours),
    tracked: parseBoolean(record.tracked) ?? false,
    created_at: parseString(record.created_at),
    updated_at: parseString(record.updated_at),
    error: parseString(record.error),
  }
}

export const parseKestrelAutoMLCatalogRows = (
  value: unknown
): KestrelAutoMLCatalogRow[] => {
  const items = parseArray<unknown>(value) ?? []
  return items
    .map(parseKestrelAutoMLCatalogRow)
    .filter((item): item is KestrelAutoMLCatalogRow => !!item)
}

export const parseKestrelDetectionTrainingData = (
  value: unknown
): KestrelDetectionTrainingData => {
  const data = parseRecord(value)
  return {
    classes: parseClasses(data.classes),
    images: parseImages(data.images),
    exports: parseKestrelTrainingDatasetExports(data.exports),
    models: parseKestrelTrainingModels(data.models),
  }
}

export class GeckoKestrelDetectionTrainingData extends GeckoEntryBase<
  KestrelDetectionTrainingData,
  { 'kestrel-model': string }
> {
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      parseKestrelDetectionTrainingData(json.data),
      {
        'kestrel-model':
          parseString(json.tags[RolodexEntryType.KESTREL_MODEL]) ?? '',
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoKestrelDetectionTrainingData(json)
  }
}
